import { axiosInstance } from "@/lib/axios";
import type { NftActivityResponse, NftHoldersResponse } from "./types";
import type { SingleNftResponse } from "@/features/nfts/types";
import { isListingLive } from "@/features/finance/listing-status";

export async function getNftActivity(
  contractAddress: string,
  tokenId: string,
  lang?: "fa" | "en"
): Promise<NftActivityResponse> {
  const { data } = await axiosInstance.get<NftActivityResponse>(
    `/nfts/${contractAddress}/${tokenId}/activity`,
    { headers: lang ? { lang } : undefined }
  );
  return data;
}

export async function getNftHolders(
  contractAddress: string,
  tokenId: string,
  lang?: "fa" | "en"
): Promise<NftHoldersResponse> {
  const { data } = await axiosInstance.get<NftHoldersResponse>(
    `/nfts/${contractAddress}/${tokenId}/holders`,
    { headers: lang ? { lang } : undefined }
  );
  return data;
}

/**
 * The NFT as the SERVER currently sees it — the second authority on whether a
 * listing has actually reached the chain.
 *
 * The page itself reads this through `serverFetch` (it is personalized, and
 * the server render needs the viewer's like/watchlist flags). This is the
 * client-side counterpart, and it exists for one job: polling after a listing
 * or a cancellation has been submitted, for when the socket is late or was
 * missed. See `features/finance/chain-settlement.ts`.
 *
 * `suppressToast`: a poll that fails is not news for the user. The caller
 * treats a failure as "no answer yet" and asks again; a toast per attempt
 * would turn a slow confirmation into a stack of errors about nothing.
 */
export async function getNftDetail(
  contractAddress: string,
  tokenId: string,
  lang?: "fa" | "en"
): Promise<SingleNftResponse> {
  const { data } = await axiosInstance.get<SingleNftResponse>(
    `/nfts/${contractAddress}/${tokenId}`,
    { headers: lang ? { lang } : undefined, suppressToast: true }
  );
  return data;
}

/**
 * Is this NFT listed, as the server sees it right now?
 *
 * The rule itself lives in `features/finance/listing-status`, and this is one
 * of its two callers — the other being the asset page, which draws the price
 * and the Cancel action from the same answer. They each had their own copy of
 * it, written against states (`active`, `listed`) that this API does not send;
 * see that module for what the real ones are and what the old rule would have
 * done to every listed NFT.
 */
export function isNftListed(response: SingleNftResponse | null | undefined): boolean {
  return isListingLive(response?.data?.item?.listing);
}